import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { fetchCourse, fetchInterventions } from "@/lib/logicApi";
import { useLessonProgress } from "@/lib/academyProgress";
import { GeometricEye, LogicImage } from "./LogicImage";
import { TermText, useLogicTerms } from "./TermChip";
import { useLogicUi } from "./logicUi";
import { useSEO } from "@/lib/useSEO";
import { DisciplineSwitcher } from "@/components/academy/DisciplineSwitcher";
import { LockedLesson } from "@/components/academy/TrialGate";
import { LessonTopNav, LessonBottomNav, CompletionPanel } from "@/components/academy/LessonNav";
import { BTN_FX, DISCIPLINE_THEME } from "@/lib/academyTheme";
import { tpl } from "@/lib/academyTheme";

const MENTOR_NAMES = { marcus: "Marcus Aurelius", epictetus: "Epictetus", seneca: "Seneca" };

function MentorNote({ mentor, node, testid, L, labels }) {
  const [depth, setDepth] = useState(0);
  if (!node) return null;
  const levels = ["plain", "academic", "scholarly"];
  return (
    <div className="mt-3 border-l-2 border-[#8fa3c8]/60 bg-[#1a2130]/70 pl-4 pr-3 py-3" data-testid={testid}>
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#8fa3c8]">{MENTOR_NAMES[mentor]}</p>
      <p className="mt-1.5 text-sm text-fog/95 font-reading leading-relaxed">{L(node[levels[depth]])}</p>
      <button type="button" onClick={() => setDepth((depth + 1) % 3)}
        className="mt-2 text-[10px] tracking-[0.2em] uppercase text-gold/70 hover:text-gold">{labels[depth]}</button>
    </div>
  );
}

function Layer({ title, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#c8a95a]/20 bg-[#f3e9d2]/[0.04]">
      <button data-testid={testid} onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full text-left px-6 py-4 flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-gold/60">
        <span className="text-xs tracking-[0.25em] uppercase text-gold/80">{title}</span>
        <span className="text-gold/60" aria-hidden>{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-6 pb-6 text-fog font-reading leading-[1.85] text-sm">{children}</div>}
    </div>
  );
}

export default function LogicLesson() {
  const { phaseSlug, lessonSlug } = useParams();
  const { ui, lang, L, t, opt } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const [course, setCourse] = useState(null);
  const [interventions, setInterventions] = useState(null);
  const [shortNote, setShortNote] = useState(false);
  const terms = useLogicTerms();
  const {
    responses, exerciseResults, setResponse, setExerciseResult,
    flush, complete, saveState, completed, lastSavedAt,
  } = useLessonProgress("logic", lessonSlug);

  useEffect(() => {
    setShortNote(false);
    fetchCourse().then(setCourse).catch(() => {});
    fetchInterventions().then(setInterventions).catch(() => {});
  }, [lessonSlug]);

  const phase = course?.phases?.find((p) => p.slug === phaseSlug);
  const lesson = phase?.lessons?.find((l) => l.slug === lessonSlug);

  useSEO({
    title: lesson ? `${L(lesson.title)} | Stoic Logic | StoicOs.ai Academy` : "Stoic Logic",
    description: lesson ? L(lesson.learningObjective) : "",
    canonical: `https://stoicos.ai/academy/logic/phase/${phaseSlug}/lesson/${lessonSlug}`,
  });

  const scenario = responses.scenarioPrompt || "";

  const saveAndComplete = async () => {
    const ok = await complete();
    if (ok) window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  if (!course) return <div className="min-h-screen bg-ink" />;
  if (course.access === "preview" || (course.access === "intro" && lesson?.locked)) return <LockedLesson discipline="logic" backTo="/academy/logic" mode={course.access} />;
  if (!phase || !lesson) return <Navigate to="/academy/logic" replace />;
  const media = course.media || {};
  const allLessons = course.phases.flatMap((p) => p.lessons.map((l) => ({ ...l, phaseSlug: p.slug })));
  const idx = allLessons.findIndex((l) => l.slug === lessonSlug);
  const prev = allLessons[idx - 1];
  const next = allLessons[idx + 1];
  const isGateway = lesson.exercise?.type === "gateway";
  const exType = lesson.exercise?.type;
  const wrongNode = interventions?.wrong_answer?.[exType]?.[lesson.mentor]
    || Object.values(interventions?.wrong_answer?.[exType] || {})[0];
  const shortNode = interventions?.short_response?.[lesson.mentor];
  const exKey = (i) => `exercise:${i}`;
  const gradedItems = (lesson.exercise?.items || []).filter((it) => it.answer !== "");
  const allCorrect = gradedItems.length > 0 && gradedItems.every((it) => {
    const i = lesson.exercise.items.indexOf(it);
    return exerciseResults[exKey(i)] === it.answer;
  });
  const anyWrong = (lesson.exercise?.items || []).some((it, i) =>
    it.answer !== "" && exerciseResults[exKey(i)] !== undefined && exerciseResults[exKey(i)] !== it.answer);

  const d = lastSavedAt ? new Date(lastSavedAt) : null;
  const savedLine =
    saveState === "saving" ? t("academy.save.saving")
    : saveState === "failed" ? t("academy.save.failed")
    : saveState === "saved" ? t("academy.save.saved")
    : d ? tpl(t("academy.save.lastSaved"), {
        date: d.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB"),
        time: d.toLocaleTimeString(lang === "nl" ? "nl-NL" : "en-GB", { hour: "2-digit", minute: "2-digit" }),
      }) : "";

  const returnCtx = { lesson: { ...lesson, phaseSlug: phase.slug }, prev, next, phaseSlug: phase.slug };

  return (
    <main className="min-h-screen bg-ink px-6 sm:px-10 py-20" data-testid="logic-lesson-page">
      <div className="max-w-3xl mx-auto space-y-14">
        {/* A. Threshold */}
        <header>
          <DisciplineSwitcher active="logic" />
          <nav aria-label="Breadcrumb" className="text-[10px] tracking-[0.25em] uppercase text-[#8fa3c8] mb-6">
            <Link to="/academy/logic" className="hover:text-gold">{L(course.title)}</Link> / <Link to={`/academy/logic/phase/${phase.slug}`} className="hover:text-gold">{L(phase.title)}</Link>
          </nav>
          <LessonTopNav discipline="logic" phase={phase} lesson={lesson} prev={prev} next={next} total={allLessons.length} locTitle={L} />
          <div className="flex items-center gap-3 mb-4">
            <GeometricEye media={media} size={28} />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8]">{ui.phase} {phase.order} · {ui.lesson} {lesson.order}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-bone">{L(lesson.title)}</h1>
          <p className="mt-5 font-serif italic text-lg text-gold/90">{L(lesson.thresholdSentence)}</p>
          <p className="mt-3 text-sm text-fog/70">{ui.skill} {L(lesson.learningObjective)}</p>
        </header>

        {/* B. Experience first */}
        <section aria-labelledby="lesson-scenario">
          <h2 id="lesson-scenario" className="text-xs tracking-[0.3em] uppercase text-[#8fa3c8] mb-4">{ui.situation}</h2>
          {lesson.assetKeys?.[0] && <div className="mb-6 max-w-lg"><LogicImage media={media} assetKey={lesson.assetKeys[0]} alt="" /></div>}
          <p className="font-reading text-fog leading-[1.95] text-base"><TermText terms={terms} text={L(lesson.openingScenario)} /></p>
          {!isGateway && (
            <div className="mt-6">
              <label htmlFor="scenario-response" className="block text-sm text-bone/90 font-reading mb-3">{L(lesson.scenarioPrompt)}</label>
              <textarea id="scenario-response" data-testid="logic-scenario-input" value={scenario} rows={4}
                onChange={(e) => { if (e.target.value.trim().length >= 40) setShortNote(false); setResponse("scenarioPrompt", e.target.value); }}
                onBlur={() => setShortNote(scenario.trim().length > 0 && scenario.trim().length < 40)}
                className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-4 focus:outline-none focus:ring-2 focus:ring-gold/50"
                placeholder={ui.writeFreely} />
              {shortNote && <MentorNote mentor={lesson.mentor} node={shortNode} testid="logic-mentor-short-response" L={L} labels={ui.depthLabels} />}
            </div>
          )}
        </section>

        {/* C+D. Core insight */}
        <section className="border-l-2 border-gold/50 pl-6 py-2">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-3">{ui.coreInsight}</p>
          <p className="font-serif text-xl text-bone leading-relaxed"><TermText terms={terms} text={L(lesson.coreInsight)} /></p>
        </section>

        {/* E. Depth layers */}
        <section className="space-y-3" aria-label="Depth layers">
          <Layer title={ui.essential} testid="logic-layer-essential"><TermText terms={terms} text={L(lesson.depthLayers?.essential)} /></Layer>
          <Layer title={ui.academicDepth} testid="logic-layer-academic"><TermText terms={terms} text={L(lesson.depthLayers?.academic)} /></Layer>
          <Layer title={ui.sourceStudy} testid="logic-layer-sources">
            {L(lesson.depthLayers?.sources)} <Link to="/library" className="text-gold hover:underline">{ui.livingLibrary}</Link>
          </Layer>
        </section>

        {/* F. Mentor question — answered inline, saved to the account */}
        <section className="border border-[#8fa3c8]/25 bg-[#1a2130] p-6" aria-label="Mentor intervention">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#8fa3c8] mb-2">{MENTOR_NAMES[lesson.mentor]}</p>
          <p className="font-serif italic text-lg text-bone/95"><TermText terms={terms} text={L(lesson.mentorQuestion)} /></p>
          {!isGateway && (
            <textarea data-testid="logic-mentor-question-input" value={responses.mentorQuestion || ""} rows={3}
              aria-label="Your answer to the mentor"
              onChange={(e) => setResponse("mentorQuestion", e.target.value)}
              className="mt-4 w-full bg-[#f3e9d2]/[0.05] border border-[#8fa3c8]/30 text-bone font-reading p-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
              placeholder={ui.writeFreely} />
          )}
        </section>

        {/* G. Exercise */}
        {!isGateway && lesson.exercise?.items?.length > 0 && (
          <section aria-labelledby="lesson-exercise">
            <h2 id="lesson-exercise" className="text-xs tracking-[0.3em] uppercase text-[#8fa3c8] mb-3">{ui.exercise}</h2>
            <p className="text-sm text-fog/85 font-reading mb-5">{L(lesson.exercise.instruction)}</p>
            <div className="space-y-4">
              {lesson.exercise.items.map((it, i) => (
                <div key={i} className="border border-[#c8a95a]/15 bg-[#171c26] p-4">
                  <p className="text-bone font-reading text-sm mb-3">{L(it.text)}</p>
                  {it.answer === "" ? (
                    <textarea rows={2} aria-label={`Your answer for item ${i + 1}`} data-testid={`logic-exercise-free-${i}`}
                      value={exerciseResults[exKey(i)] || ""}
                      onChange={(e) => setExerciseResult(exKey(i), e.target.value)}
                      className="w-full bg-transparent border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(lesson.exercise.type === "assent" ? ["assent", "withhold", "reject"]
                        : lesson.exercise.type === "evidence" ? ["supports", "undermines", "irrelevant"]
                        : lesson.exercise.type === "argument" ? ["valid", "invalid"]
                        : lesson.exercise.type === "fallacy" ? ["false dilemma", "personal attack", "appeal to emotion", "contradiction"]
                        : lesson.exercise.type === "chain" ? ["0","1","2","3","4","5","6"]
                        : ["fact", "added"]).map((o) => {
                        const chosen = exerciseResults[exKey(i)];
                        const correct = chosen !== undefined && chosen === it.answer;
                        return (
                          <button key={o} data-testid={`logic-exercise-${i}-${o.replace(/\s/g, "-")}`}
                            onClick={() => setExerciseResult(exKey(i), o)}
                            aria-pressed={chosen === o}
                            className={`px-3 py-2 text-[11px] tracking-[0.14em] uppercase border transition-colors focus:outline-none focus:ring-2 focus:ring-gold/60 ${chosen === o ? (correct ? "border-gold bg-gold/15 text-gold" : "border-[#8fa3c8] bg-[#2e3a50] text-[#b9c6de]") : "border-white/15 text-fog/70 hover:border-gold/40"}`}>
                            {lesson.exercise.type === "chain" ? `${+o + 1}` : opt(o)}
                          </button>
                        );
                      })}
                      {exerciseResults[exKey(i)] !== undefined && exerciseResults[exKey(i)] === it.answer && (
                        <span className="text-[11px] self-center ml-2 text-fog/70" data-testid={`logic-exercise-${i}-feedback`}>
                          {ui.distinguished}
                        </span>
                      )}
                    </div>
                  )}
                  {it.answer !== "" && exerciseResults[exKey(i)] !== undefined && exerciseResults[exKey(i)] !== it.answer && (
                    <MentorNote mentor={lesson.mentor} node={wrongNode} testid={`logic-mentor-correction-${i}`} L={L} labels={ui.depthLabels} />
                  )}
                </div>
              ))}
            </div>
            {allCorrect && !anyWrong && interventions?.exercise_complete?.[lesson.mentor] && (
              <p className="mt-5 border-l-2 border-gold/50 pl-4 py-1 font-serif italic text-bone/90" data-testid="logic-mentor-complete">
                <span className="not-italic text-[10px] tracking-[0.25em] uppercase text-[#8fa3c8] block mb-1">{MENTOR_NAMES[lesson.mentor]}</span>
                {L(interventions.exercise_complete[lesson.mentor])}
              </p>
            )}
          </section>
        )}

        {/* H. Practice beyond the Academy */}
        <section className="border border-[#c8a95a]/30 bg-[#f3e9d2]/[0.06] p-6" aria-labelledby="lesson-practice">
          <h2 id="lesson-practice" className="text-xs tracking-[0.3em] uppercase text-gold/80 mb-3">{ui.practiceBeyond}</h2>
          <p className="font-reading text-fog leading-[1.9] text-sm">{L(lesson.practiceAssignment)}</p>
          <textarea data-testid="logic-practice-input" value={responses.practiceAssignment || ""} rows={3}
            aria-label="Your practice record"
            onChange={(e) => setResponse("practiceAssignment", e.target.value)}
            className="mt-5 w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
            placeholder={lang === "nl" ? "Noteer hier wat je in de praktijk hebt gedaan of waargenomen…" : "Record here what you did or observed in practice…"} />
        </section>

        {/* I. Reflection — one field per question, canonical reflection:{i} keys */}
        <section aria-labelledby="lesson-reflection">
          <h2 id="lesson-reflection" className="text-xs tracking-[0.3em] uppercase text-[#8fa3c8] mb-3">{ui.reflection}</h2>
          <div className="space-y-6">
            {(lesson.reflectionQuestions || []).map((q, i) => (
              <div key={i}>
                <p className="text-sm text-fog/80 font-reading italic mb-2">{L(q)}</p>
                <textarea data-testid={`logic-reflection-input-${i}`} value={responses[`reflection:${i}`] || ""} rows={4}
                  aria-label={`Your private reflection ${i + 1}`}
                  onChange={(e) => setResponse(`reflection:${i}`, e.target.value)}
                  className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-4 focus:outline-none focus:ring-2 focus:ring-gold/50" />
              </div>
            ))}
          </div>
        </section>

        {/* J. Save actions */}
        {!isGateway && (
          <section className="border-t border-white/10 pt-6 space-y-4" aria-label="Save actions">
            <p aria-live="polite" data-testid="logic-save-status"
              className={`text-[11px] h-4 ${saveState === "failed" ? "text-red-300" : "text-gold/70"}`}>{savedLine}</p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="logic-save-draft" onClick={() => flush()}
                className={`min-h-[48px] px-6 border border-[#8fa3c8]/50 text-[#b9c6de] text-[11px] tracking-[0.2em] uppercase hover:bg-[#2e3a50]/40 focus:outline-none focus-visible:ring-2 ${th.ring}`}>
                {t("academy.save.draftBtn")}
              </button>
              <button data-testid="logic-save-complete" onClick={saveAndComplete} disabled={completed}
                className={`min-h-[48px] px-6 text-[11px] font-semibold tracking-[0.2em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
                {t("academy.save.completeBtn")}
              </button>
            </div>
            {completed && (
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#8fa3c8]" data-testid="logic-lesson-completed-note">
                ✓ {t("academy.save.completedNote")}
              </p>
            )}
            {(saveState === "saved" || completed) && (
              <p className="text-[11px] tracking-[0.18em] uppercase">
                <Link to="/academy/my-work" className="text-[#b9c6de] hover:text-gold" data-testid="logic-lesson-mywork-link">{t("academy.nav.myWork")} →</Link>
              </p>
            )}
          </section>
        )}

        {completed && <CompletionPanel discipline="logic" returnCtx={returnCtx} locTitle={L} />}

        {/* K. Continue */}
        <p className="text-[11px] tracking-[0.2em] uppercase">
          <Link to={`/academy/logic/journal?fromPhase=${phase.slug}&fromLesson=${lesson.slug}`} className="text-[#b9c6de] hover:text-gold" data-testid="logic-lesson-journal-link">{ui.openRecord}</Link>
        </p>
        <LessonBottomNav discipline="logic" phase={phase} lesson={{ ...lesson, phaseSlug: phase.slug }} prev={prev} next={next} locTitle={L} />
      </div>
    </main>
  );
}
